import React, { useState, useEffect } from 'react';
import { X, Download, Smartphone } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * Component to show install prompt for first-time users
 */
const InstallPrompt = ({ isInstallable, onInstall, onDismiss }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Check if user has already been prompted
    const hasBeenPrompted = localStorage.getItem('pwa-install-prompted');
    
    // Show prompt only if:
    // 1. App is installable
    // 2. User hasn't been prompted before
    // 3. User is on mobile/tablet
    const isMobileOrTablet = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    if (isInstallable && !hasBeenPrompted && isMobileOrTablet) {
      // Show prompt after a short delay (2 seconds after login)
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [isInstallable]);

  const handleInstall = async () => {
    const success = await onInstall();
    if (success) {
      localStorage.setItem('pwa-install-prompted', 'true');
      localStorage.setItem('pwa-install-accepted', 'true');
      setIsVisible(false);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem('pwa-install-prompted', 'true');
    localStorage.setItem('pwa-install-dismissed', 'true');
    setIsVisible(false);
    if (onDismiss) onDismiss();
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 space-y-4 transform transition-all animate-in slide-in-from-bottom duration-300">
        {/* Close button */}
        <button
          onClick={handleDismiss}
          className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-full transition-colors"
          aria-label="Close"
        >
          <X className="w-5 h-5 text-gray-500" />
        </button>

        {/* Icon */}
        <div className="flex justify-center mb-4">
          <div className="bg-teal-100 p-4 rounded-full">
            <Smartphone className="w-12 h-12 text-teal-600" />
          </div>
        </div>

        {/* Content */}
        <div className="text-center space-y-2">
          <h2 className="text-2xl font-bold text-gray-900">
            Install Reuseabox App
          </h2>
          <p className="text-gray-600">
            Get quick access from your home screen. No app store needed!
          </p>
        </div>

        {/* Benefits */}
        <div className="space-y-2 text-sm text-gray-600">
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 bg-teal-500 rounded-full"></div>
            <span>Instant access from your home screen</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 bg-teal-500 rounded-full"></div>
            <span>Works offline for basic features</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 bg-teal-500 rounded-full"></div>
            <span>Faster loading times</span>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex gap-3 pt-4">
          <Button
            onClick={handleDismiss}
            variant="outline"
            className="flex-1"
          >
            Not Now
          </Button>
          <Button
            onClick={handleInstall}
            className="flex-1 bg-teal-600 hover:bg-teal-700 text-white"
          >
            <Download className="w-4 h-4 mr-2" />
            Install
          </Button>
        </div>
      </div>
    </div>
  );
};

export default InstallPrompt;
